/**
 * Painel de administração.
 *
 * A pergunta central para quem hospeda numa VPS com franquia de tráfego é
 * simples: vou estourar a cota deste mês? Por isso a projeção de consumo abre o
 * painel, antes de qualquer outra métrica. O resto responde "por quê": quais
 * salas estão ativas, quem consome mais, como está a máquina.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  ArrowLeft,
  Cpu,
  Database,
  Hash,
  HardDrive,
  MessageSquare,
  MonitorUp,
  Radio,
  RefreshCw,
  Settings2,
  Shield,
  Users,
  Wifi,
} from 'lucide-react'
import type { AdminOverview, AdminUserRow, MetricsRange, VoiceRoomStat } from '@nexus/shared'
import { formatBitrate, formatBytes, formatDuration, formatNumber } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api, ApiError } from '@/lib/api'
import { useInterval } from '@/hooks/useInterval'
import { Badge, Button, Field, Input, Spinner, Switch, Slider } from '@/components/ui'
import { AreaChart, BarRow, QuotaGauge, StatTile, type SeriesPoint } from '@/components/admin/Charts'

const RANGES: { id: MetricsRange; label: string }[] = [
  { id: '1h', label: '1 hora' },
  { id: '6h', label: '6 horas' },
  { id: '24h', label: '24 horas' },
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
]

type Tab = 'overview' | 'voice' | 'guilds' | 'users' | 'settings'

export function AdminView() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [range, setRange] = useState<MetricsRange>('24h')

  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    setRefreshing(true)
    try {
      setOverview(await api.admin.overview(range))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar as métricas')
    } finally {
      setRefreshing(false)
    }
  }, [range])

  useEffect(() => {
    void load()
  }, [load])

  /*
    Atualização automática nas abas que mostram o agora.

    Métrica que envelhece na tela é pior do que métrica ausente, porque parece
    atual. Três segundos acompanham bem quem entra e sai de uma chamada, que é o
    que muda de verdade enquanto alguém olha o painel; as demais abas mostram
    dados históricos e não ganham nada em recarregar sozinhas.
  */
  const aoVivo = tab === 'overview' || tab === 'voice'
  useInterval(() => void load(), aoVivo ? 3_000 : null)

  const tabs: { id: Tab; label: string; icon: typeof Activity }[] = [
    { id: 'overview', label: 'Visão geral', icon: Activity },
    { id: 'voice', label: 'Voz e banda', icon: Radio },
    { id: 'guilds', label: 'Servidores', icon: Hash },
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'settings', label: 'Configurações', icon: Settings2 },
  ]

  return (
    <div className="flex h-dvh flex-col bg-surface-base">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line px-5">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-content-secondary transition-colors hover:text-content-primary"
        >
          <ArrowLeft className="size-4" />
          Voltar
        </button>

        <div className="h-5 w-px bg-line" />

        <div className="flex items-center gap-2">
          <Shield className="size-5 text-warning" />
          <h1 className="font-semibold text-content-primary">Administração</h1>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Deixa explícito que os números se atualizam sozinhos, senão a
              pessoa fica clicando em atualizar sem necessidade. */}
          {aoVivo ? (
            <span className="flex items-center gap-1.5 rounded-full bg-success-subtle px-2.5 py-1 text-[11px] font-medium text-success">
              <span className="relative flex size-1.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-success" />
              </span>
              Ao vivo
            </span>
          ) : null}

          <select
            value={range}
            onChange={(e) => setRange(e.target.value as MetricsRange)}
            className="h-8 rounded-md bg-surface-raised px-2 text-[13px] outline-none"
          >
            {RANGES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>

          <Button size="icon-sm" variant="ghost" onClick={() => void load()} aria-label="Atualizar">
            <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
          </Button>
        </div>
      </header>

      {/*
        A coluna de abas vira uma faixa rolável no topo quando a tela é estreita.
        Duzentos pixels de menu ao lado deixariam menos da metade da largura de
        um celular para o relatório em si, que é o que se veio ver.
      */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-line p-2 lg:w-52 lg:flex-col lg:border-r lg:border-b-0 lg:p-3">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={cn(
                'flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                'whitespace-nowrap lg:w-full',
                tab === id
                  ? 'bg-surface-raised text-content-primary'
                  : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
              )}
            >
              <Icon className="size-4" />
              {label}
            </button>
          ))}

          {/* O resumo de saúde só cabe na coluna; na faixa ele viraria um bloco
              alto empurrando o conteúdo para fora da primeira tela. */}
          {overview ? (
            <div className="hidden lg:block">
              <HealthPanel overview={overview} />
            </div>
          ) : null}
        </nav>

        <main className="min-w-0 flex-1 overflow-y-auto p-3 sm:p-5">
          {error ? (
            <div className="mb-4 rounded-lg bg-danger-subtle p-3 text-sm text-danger">{error}</div>
          ) : null}

          {!overview ? (
            <div className="flex h-64 items-center justify-center">
              <Spinner className="size-8 text-brand" />
            </div>
          ) : tab === 'overview' ? (
            <OverviewTab overview={overview} range={range} />
          ) : tab === 'voice' ? (
            <VoiceTab overview={overview} range={range} />
          ) : tab === 'guilds' ? (
            <GuildsTab />
          ) : tab === 'users' ? (
            <UsersTab />
          ) : (
            <SettingsTab />
          )}
        </main>
      </div>
    </div>
  )
}

function HealthPanel({ overview }: { overview: AdminOverview }) {
  const { metrics } = overview
  const memoryPercent = Math.round(
    ((metrics.host.totalMemoryMb - metrics.host.freeMemoryMb) / metrics.host.totalMemoryMb) * 100,
  )

  return (
    <div className="mt-6 rounded-lg bg-surface-raised p-3">
      <h3 className="mb-2 text-[10px] font-bold tracking-wider text-content-muted uppercase">
        Servidor
      </h3>

      <MiniStat label="CPU do host" value={`${metrics.host.cpuPercent.toFixed(0)}%`} percent={metrics.host.cpuPercent} />
      <MiniStat label="Memória" value={`${memoryPercent}%`} percent={memoryPercent} />
      <MiniStat
        label="Processo"
        value={`${metrics.process.memoryMb} MB`}
        percent={(metrics.process.memoryMb / metrics.host.totalMemoryMb) * 100}
      />

      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-content-muted">Ativo há</span>
        <span className="text-content-secondary">{formatDuration(metrics.uptimeSeconds)}</span>
      </div>

      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className="text-content-muted">Latência do loop</span>
        <span
          className={cn(
            metrics.process.eventLoopLagMs > 50 ? 'text-warning' : 'text-content-secondary',
          )}
        >
          {metrics.process.eventLoopLagMs.toFixed(1)} ms
        </span>
      </div>
    </div>
  )
}

function MiniStat({ label, value, percent }: { label: string; value: string; percent: number }) {
  const color =
    percent > 85 ? 'var(--color-danger)' : percent > 65 ? 'var(--color-warning)' : 'var(--color-success)'

  return (
    <div className="mb-2">
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="text-content-muted">{label}</span>
        <span className="text-content-secondary tabular-nums">{value}</span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-surface-active">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.min(100, percent)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  )
}

function OverviewTab({ overview, range }: { overview: AdminOverview; range: MetricsRange }) {
  const { totals, online, metrics, bandwidth } = overview
  const [systemSeries, setSystemSeries] = useState<SeriesPoint[]>([])

  useEffect(() => {
    void api.admin
      .systemSeries(range)
      .then(({ series }) =>
        setSystemSeries(
          series.map((s) => ({
            label: new Date(s.bucket).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            }),
            value: s.activeConnections,
            secondary: s.activeVoiceParticipants,
          })),
        ),
      )
      .catch(() => setSystemSeries([]))
  }, [range])

  const bandwidthSeries: SeriesPoint[] = bandwidth.series.map((point) => ({
    label: new Date(point.bucket).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
    value: point.egressBytes,
    secondary: point.ingressBytes,
  }))

  const projectedPercent =
    bandwidth.monthlyQuotaBytes > 0
      ? (bandwidth.projectedMonthEgressBytes / bandwidth.monthlyQuotaBytes) * 100
      : 0

  const willExceed = projectedPercent > 100

  return (
    <div className="flex flex-col gap-5">
      {/* A projeção de cota vem primeiro porque é a métrica que muda decisões. */}
      <section className="rounded-xl bg-surface-raised p-5">
        <div className="flex flex-wrap items-center gap-6">
          <QuotaGauge
            usedPercent={bandwidth.quotaUsedPercent}
            projectedPercent={projectedPercent}
            label="da cota"
            sublabel={`${bandwidth.daysElapsed.toFixed(0)} de ${bandwidth.daysInMonth} dias`}
          />

          <div className="min-w-64 flex-1">
            <h2 className="text-sm font-semibold text-content-primary">Tráfego de saída do mês</h2>

            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-[11px] text-content-muted">Consumido</dt>
                <dd className="font-semibold text-content-primary tabular-nums">
                  {formatBytes(bandwidth.monthToDateEgressBytes)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-content-muted">Franquia</dt>
                <dd className="font-semibold text-content-primary tabular-nums">
                  {formatBytes(bandwidth.monthlyQuotaBytes)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-content-muted">Projeção do mês</dt>
                <dd
                  className={cn(
                    'font-semibold tabular-nums',
                    willExceed ? 'text-danger' : 'text-content-primary',
                  )}
                >
                  {formatBytes(bandwidth.projectedMonthEgressBytes)}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-content-muted">Saída agora</dt>
                <dd className="font-semibold text-content-primary tabular-nums">
                  {formatBitrate(metrics.voice.egressBps)}
                </dd>
              </div>
            </dl>

            <p
              className={cn(
                'mt-3 rounded-lg p-2.5 text-[13px] leading-relaxed',
                willExceed
                  ? 'bg-danger-subtle text-danger'
                  : bandwidth.quotaUsedPercent > 60
                    ? 'bg-warning-subtle text-warning'
                    : 'bg-success-subtle text-success',
              )}
            >
              {willExceed
                ? `No ritmo atual a franquia será ultrapassada em ${formatBytes(bandwidth.projectedMonthEgressBytes - bandwidth.monthlyQuotaBytes)}. Reduza o teto de qualidade ou ative a redução automática.`
                : bandwidth.quotaUsedPercent > 60
                  ? 'Consumo acima da metade da franquia. Acompanhe as salas com transmissão de tela.'
                  : 'Consumo dentro do previsto para o mês.'}
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Pessoas online"
          value={formatNumber(online.total)}
          sublabel={`${online.inVoice} em chamada`}
          accent="success"
          icon={<Users />}
        />
        <StatTile
          label="Compartilhando tela"
          value={formatNumber(online.screenSharing)}
          sublabel={`${metrics.voice.activeRooms} ${metrics.voice.activeRooms === 1 ? 'sala ativa' : 'salas ativas'}`}
          accent="brand"
          icon={<MonitorUp />}
        />
        <StatTile
          label="Mensagens em 24h"
          value={formatNumber(totals.messagesLast24h)}
          sublabel={`${formatNumber(totals.messages)} no total`}
          icon={<MessageSquare />}
        />
        <StatTile
          label="Armazenamento"
          value={formatBytes(totals.storageBytes)}
          sublabel="anexos e avatares"
          accent="warning"
          icon={<HardDrive />}
        />
      </section>

      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Tráfego por período</h2>
        <p className="mb-3 text-xs text-content-muted">
          Saída é o que conta para a franquia do provedor. Entrada é o que os clientes enviam ao
          servidor.
        </p>
        <AreaChart
          data={bandwidthSeries}
          formatValue={(v) => formatBytes(v, 0)}
          primaryLabel="Saída"
          secondaryLabel="Entrada"
          height={200}
        />
      </section>

      <section className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl bg-surface-raised p-5">
          <h2 className="mb-3 text-sm font-semibold text-content-primary">Conexões simultâneas</h2>
          <AreaChart
            data={systemSeries}
            primaryLabel="Conexões"
            secondaryLabel="Em chamada"
            color="var(--color-success)"
            height={160}
          />
        </div>

        <div className="rounded-xl bg-surface-raised p-5">
          <h2 className="mb-3 text-sm font-semibold text-content-primary">Recursos</h2>

          <div className="flex flex-col gap-3">
            <ResourceRow
              icon={<Cpu className="size-4" />}
              label="CPU do processo"
              value={`${metrics.process.cpuPercent.toFixed(1)}%`}
              percent={metrics.process.cpuPercent / metrics.host.cpuCount}
            />
            <ResourceRow
              icon={<Activity className="size-4" />}
              label="Memória do processo"
              value={`${metrics.process.memoryMb} MB`}
              percent={(metrics.process.memoryMb / metrics.host.totalMemoryMb) * 100}
            />
            <ResourceRow
              icon={<Database className="size-4" />}
              label="Conexões do banco"
              // Em uso sobre o teto. O driver abre conexão sob demanda, então
              // servidor parado mostra zero, e não o conjunto inteiro ocupado.
              value={`${metrics.database.poolInUse}/${metrics.database.poolTotal}`}
              percent={(metrics.database.poolInUse / metrics.database.poolTotal) * 100}
            />
            <ResourceRow
              icon={<Wifi className="size-4" />}
              label="Eventos por minuto"
              value={formatNumber(metrics.gateway.messagesOutPerMin)}
              percent={Math.min(100, metrics.gateway.messagesOutPerMin / 100)}
            />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-2 border-t border-line pt-3 text-xs">
            <div>
              <dt className="text-content-muted">Núcleos</dt>
              <dd className="text-content-secondary">{metrics.host.cpuCount}</dd>
            </div>
            <div>
              <dt className="text-content-muted">Memória total</dt>
              <dd className="text-content-secondary">
                {(metrics.host.totalMemoryMb / 1024).toFixed(1)} GB
              </dd>
            </div>
            <div>
              <dt className="text-content-muted">Usuários</dt>
              <dd className="text-content-secondary">{formatNumber(totals.users)}</dd>
            </div>
            <div>
              <dt className="text-content-muted">Servidores</dt>
              <dd className="text-content-secondary">{formatNumber(totals.guilds)}</dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  )
}

/**
 * De onde a pessoa acessa.
 *
 * Só aparece enquanto ela está conectada: a origem é uma característica da
 * conexão, não da conta, e mostrar a de ontem como se fosse a de agora seria
 * pior do que não mostrar nada.
 */
function Origem({ user }: { user: AdminUserRow }) {
  if (!user.pais && !user.cidade) {
    /*
      Sem país, mostra o endereço que chegou ao servidor.

      É o que separa "essa pessoa não está conectada" de "o proxy está
      entregando o endereço dele mesmo em vez do de quem visita", e a segunda
      leitura é impossível de tirar de uma célula vazia.
    */
    return (
      <span className="text-[13px] text-content-muted tabular-nums">{user.ip ?? '—'}</span>
    )
  }

  const detalhe = [user.cidade, user.regiao].filter(Boolean).join(", ")

  return (
    <div className="min-w-0">
      <p className="truncate text-[13px] text-content-primary">
        {user.pais}
        {user.paisCodigo ? (
          <span className="ml-1.5 text-[11px] text-content-muted">{user.paisCodigo}</span>
        ) : null}
      </p>
      {detalhe ? <p className="truncate text-[11px] text-content-muted">{detalhe}</p> : null}
    </div>
  )
}

/**
 * Ida e volta até o servidor.
 *
 * As faixas de cor existem para a leitura ser de relance numa lista longa. O
 * número fica visível de qualquer jeito, porque o que separa uma conexão boa de
 * uma ruim depende de onde a pessoa está, e isso o painel não decide sozinho.
 */
function Ping({ valor }: { valor: number | null }) {
  if (valor === null) {
    return <span className="text-[13px] text-content-muted">—</span>
  }

  const cor =
    valor < 80
      ? "text-success"
      : valor < 200
        ? "text-content-primary"
        : valor < 400
          ? "text-warning"
          : "text-danger"

  return <span className={cn("text-[13px] tabular-nums", cor)}>{valor} ms</span>
}

function ResourceRow({
  icon,
  label,
  value,
  percent,
}: {
  icon: React.ReactNode
  label: string
  value: string
  percent: number
}) {
  const color =
    percent > 85 ? 'var(--color-danger)' : percent > 65 ? 'var(--color-warning)' : 'var(--color-brand)'

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-content-muted">{icon}</span>
        <span className="flex-1 text-[13px] text-content-secondary">{label}</span>
        <span className="font-mono text-xs text-content-primary tabular-nums">{value}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-active">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  )
}

function VoiceTab({ overview, range }: { overview: AdminOverview; range: MetricsRange }) {
  const [rooms, setRooms] = useState<VoiceRoomStat[]>([])
  const [usage, setUsage] = useState<
    {
      userId: string
      displayName: string
      sessions: number
      totalSeconds: number
      screenShareSeconds: number
      egressBytes: number
    }[]
  >([])

  const load = useCallback(async () => {
    const [roomsResult, usageResult] = await Promise.allSettled([
      api.admin.voiceRooms(),
      api.admin.voiceUsage(range),
    ])
    if (roomsResult.status === 'fulfilled') setRooms(roomsResult.value.rooms)
    if (usageResult.status === 'fulfilled') setUsage(usageResult.value.usage)
  }, [range])

  useEffect(() => {
    void load()
  }, [load])

  // Acompanha o mesmo ritmo da visão geral: as salas mudam de participante o
  // tempo todo, e é justamente isso que se olha aqui.
  useInterval(() => void load(), 3_000)

  const maxSeconds = Math.max(...usage.map((u) => u.totalSeconds), 1)

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-3 sm:grid-cols-3">
        <StatTile
          label="Salas ativas"
          value={formatNumber(overview.metrics.voice.activeRooms)}
          sublabel={`${overview.metrics.voice.activeParticipants} participantes`}
          accent="success"
          icon={<Radio />}
        />
        <StatTile
          label="Saída de mídia"
          value={formatBitrate(overview.metrics.voice.egressBps)}
          sublabel={`entrada ${formatBitrate(overview.metrics.voice.ingressBps)}`}
          accent="brand"
          icon={<Wifi />}
        />
        <StatTile
          label="Transmissões de tela"
          value={formatNumber(overview.online.screenSharing)}
          sublabel="agora"
          accent="warning"
          icon={<MonitorUp />}
        />
      </section>

      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-3 text-sm font-semibold text-content-primary">Salas em andamento</h2>

        {rooms.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-muted">
            Nenhuma sala de voz ativa no momento.
          </p>
        ) : (
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-content-muted uppercase">
                <th className="pb-2 font-semibold">Canal</th>
                <th className="pb-2 font-semibold">Servidor</th>
                <th className="pb-2 text-right font-semibold">Pessoas</th>
                <th className="pb-2 text-right font-semibold">Telas</th>
                <th className="pb-2 text-right font-semibold">Saída</th>
                <th className="pb-2 text-right font-semibold">Desde</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <tr key={room.channelId} className="border-b border-line last:border-0">
                  <td className="py-2.5 text-content-primary">{room.channelName}</td>
                  <td className="py-2.5 text-content-secondary">{room.guildName}</td>
                  <td className="py-2.5 text-right text-content-secondary tabular-nums">
                    {room.participants}
                  </td>
                  <td className="py-2.5 text-right tabular-nums">
                    {room.screenShares > 0 ? (
                      <Badge variant="success">{room.screenShares}</Badge>
                    ) : (
                      <span className="text-content-muted">0</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right text-content-secondary tabular-nums">
                    {formatBitrate(room.egressBps)}
                  </td>
                  <td className="py-2.5 text-right text-content-muted tabular-nums">
                    {new Date(room.startedAt).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <p className="mt-3 text-[11px] leading-relaxed text-content-muted">
          A saída por sala é uma estimativa: o SFU reporta banda por processo, não por sala. O
          total medido é distribuído pelo peso de cada uma, considerando participantes e
          transmissões de tela. O número agregado, esse sim, é medido.
        </p>
      </section>

      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Tempo em chamada</h2>
        <p className="mb-3 text-xs text-content-muted">
          Quem mais usa voz no período selecionado.
        </p>

        {usage.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-muted">
            Nenhuma sessão de voz registrada no período.
          </p>
        ) : (
          <div>
            {usage.slice(0, 12).map((entry) => (
              <BarRow
                key={entry.userId}
                label={entry.displayName}
                value={entry.totalSeconds}
                max={maxSeconds}
                formatValue={(v) => formatDuration(v)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function UsersTab() {
  const [users, setUsers] = useState<AdminUserRow[]>([])
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [localizacaoAtiva, setLocalizacaoAtiva] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.admin.users({ search: search || undefined, page, pageSize: 25 })
      setUsers(result.users)
      setTotal(result.total)
      setLocalizacaoAtiva(result.localizacaoAtiva)
    } finally {
      setLoading(false)
    }
  }, [search, page])

  useEffect(() => {
    // Espera o usuário parar de digitar antes de consultar.
    const timer = setTimeout(() => void load(), 300)
    return () => clearTimeout(timer)
  }, [load])

  const toggle = async (user: AdminUserRow, patch: { isAdmin?: boolean; suspended?: boolean }) => {
    try {
      await api.admin.updateUser(user.id, patch)
      await load()
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Não foi possível atualizar')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Uma coluna vazia por falta de configuração precisa dizer isso, senão
          vira uma investigação de por que a informação não aparece. */}
      {!localizacaoAtiva ? (
        <p className="rounded-lg bg-warning-subtle px-3 py-2 text-[13px] text-warning">
          A coluna de origem depende da variável IP_API_KEY, que não está configurada neste
          servidor. O ping continua funcionando sem ela.
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          placeholder="Buscar por nome, usuário ou e-mail"
          className="max-w-sm"
        />
        <span className="text-sm text-content-muted">{formatNumber(total)} contas</span>
      </div>

      <div className="overflow-hidden rounded-xl bg-surface-raised">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-6 text-content-muted" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-content-muted uppercase">
                <th className="px-4 py-3 font-semibold">Usuário</th>
                <th className="px-4 py-3 font-semibold">De onde acessa</th>
                <th className="px-4 py-3 text-right font-semibold">Ping</th>
                <th className="px-4 py-3 text-right font-semibold">Mensagens</th>
                <th className="px-4 py-3 text-right font-semibold">Servidores</th>
                <th className="px-4 py-3 font-semibold">Visto por último</th>
                <th className="px-4 py-3 text-center font-semibold">Admin</th>
                <th className="px-4 py-3 text-center font-semibold">Ativo</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium text-content-primary">{user.displayName}</p>
                    <p className="text-[11px] text-content-muted">
                      @{user.username} · {user.email}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Origem user={user} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Ping valor={user.pingMs} />
                  </td>
                  <td className="px-4 py-3 text-right text-content-secondary tabular-nums">
                    {formatNumber(user.messageCount)}
                  </td>
                  <td className="px-4 py-3 text-right text-content-secondary tabular-nums">
                    {user.guildCount}
                  </td>
                  <td className="px-4 py-3 text-content-muted">
                    {user.lastSeenAt
                      ? new Date(user.lastSeenAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'nunca'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={user.isAdmin}
                        onChange={(e) => void toggle(user, { isAdmin: e.target.checked })}
                        className="size-4 accent-[var(--color-brand)]"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={!user.suspended}
                        onChange={(e) => void toggle(user, { suspended: !e.target.checked })}
                        className="size-4 accent-[var(--color-success)]"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {total > 25 ? (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-content-muted">
            página {page} de {Math.ceil(total / 25)}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= Math.ceil(total / 25)}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function SettingsTab() {
  const [settings, setSettings] = useState<Awaited<ReturnType<typeof api.admin.settings>> | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    void api.admin.settings().then(setSettings).catch(() => {})
  }, [])

  if (!settings) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="size-6 text-content-muted" />
      </div>
    )
  }

  const patch = (changes: Partial<typeof settings>) => setSettings({ ...settings, ...changes })

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.admin.updateSettings({
        registrationMode: settings.registrationMode,
        monthlyEgressQuotaBytes: settings.monthlyEgressQuotaBytes,
        egressAlertPercent: settings.egressAlertPercent,
        autoDowngradeQuality: settings.autoDowngradeQuality,
        maxScreenShareBitrate: settings.maxScreenShareBitrate,
        maxScreenShareFramerate: settings.maxScreenShareFramerate,
        maxCameraBitrate: settings.maxCameraBitrate,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  const quotaTb = settings.monthlyEgressQuotaBytes / 1024 ** 4

  return (
    <div className="flex max-w-2xl flex-col gap-5">
      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Cadastro</h2>
        <p className="mb-3 text-xs text-content-muted">Quem pode criar conta nesta instalação.</p>

        <div className="flex flex-col gap-2">
          {(
            [
              { id: 'open', label: 'Aberto', description: 'Qualquer pessoa pode se cadastrar.' },
              {
                id: 'invite_only',
                label: 'Somente com convite',
                description: 'É necessário um código de convite válido.',
              },
              { id: 'closed', label: 'Fechado', description: 'Novos cadastros bloqueados.' },
            ] as const
          ).map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => patch({ registrationMode: option.id })}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                settings.registrationMode === option.id
                  ? 'border-brand bg-brand-subtle'
                  : 'border-line bg-surface-base hover:border-line-strong',
              )}
            >
              <p className="text-sm font-medium text-content-primary">{option.label}</p>
              <p className="text-xs text-content-secondary">{option.description}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Franquia de banda</h2>
        <p className="mb-4 text-xs leading-relaxed text-content-muted">
          A cota do provedor. O Always Free da Oracle entrega 10 TB de saída por mês. Quando o
          consumo passa do alerta, o servidor pode reduzir a qualidade sozinho.
        </p>

        <Field label="Franquia mensal">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              step={0.5}
              value={quotaTb}
              onChange={(e) =>
                patch({ monthlyEgressQuotaBytes: Math.round(Number(e.target.value) * 1024 ** 4) })
              }
              className="max-w-32"
            />
            <span className="text-sm text-content-secondary">TB por mês</span>
          </div>
        </Field>

        <div className="mt-3">
          <Slider
            value={settings.egressAlertPercent}
            onChange={(v) => patch({ egressAlertPercent: v })}
            min={50}
            max={95}
            label="Alertar em"
            formatValue={(v) => `${v}% da franquia`}
          />
        </div>

        <Switch
          checked={settings.autoDowngradeQuality}
          onChange={(v) => patch({ autoDowngradeQuality: v })}
          label="Reduzir qualidade automaticamente"
          description="Ao passar do alerta, as transmissões passam a sair em 720p a 15 quadros, qualquer que seja o perfil escolhido, até a franquia virar o mês."
        />
      </section>

      <section className="rounded-xl bg-surface-raised p-5">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Teto de qualidade</h2>
        <p className="mb-4 text-xs leading-relaxed text-content-muted">
          Limite máximo aplicado a todos. Numa sala com dez pessoas, cada transmissão de tela sai
          multiplicada por nove no consumo do servidor.
        </p>

        <ControleDeTaxa
          label="Taxa máxima da tela"
          value={settings.maxScreenShareBitrate}
          onChange={(v) => patch({ maxScreenShareBitrate: v })}
          min={100_000}
          teto={50_000_000}
        />

        <div className="py-2">
          <span className="mb-2 block text-sm font-medium text-content-primary">
            Resolução máxima
          </span>
          <div className="grid grid-cols-4 gap-2">
            {RESOLUCOES.map((opcao) => (
              <button
                key={opcao.altura}
                type="button"
                onClick={() => patch({ maxScreenShareHeight: opcao.altura })}
                className={cn(
                  'rounded-lg border p-2 text-center transition-colors',
                  settings.maxScreenShareHeight === opcao.altura
                    ? 'border-brand bg-brand-subtle'
                    : 'border-line bg-surface-base hover:border-line-strong',
                )}
              >
                <span className="block text-sm font-medium text-content-primary">
                  {opcao.rotulo}
                </span>
                <span className="block text-[11px] text-content-muted">{opcao.taxaSugerida}</span>
              </button>
            ))}
          </div>
        </div>

        <Slider
          value={settings.maxScreenShareFramerate}
          onChange={(v) => patch({ maxScreenShareFramerate: v })}
          min={5}
          max={120}
          step={5}
          label="Quadros por segundo"
          formatValue={(v) => `${v} fps`}
        />

        <ControleDeTaxa
          label="Taxa máxima da câmera"
          value={settings.maxCameraBitrate}
          onChange={(v) => patch({ maxCameraBitrate: v })}
          min={100_000}
          teto={25_000_000}
        />

        <ProjecaoDeConsumo
          bitrate={settings.maxScreenShareBitrate}
          altura={settings.maxScreenShareHeight}
          franquiaBytes={Number(settings.monthlyEgressQuotaBytes)}
        />
      </section>

      <div className="flex items-center justify-end gap-3">
        {saved ? <span className="text-sm text-success">Configurações salvas</span> : null}
        <Button onClick={() => void handleSave()} loading={saving}>
          Salvar configurações
        </Button>
      </div>
    </div>
  )
}

/**
 * Controle de taxa com valor livre.
 *
 * A régua serve para o ajuste rápido na faixa em que quase todo mundo fica, e o
 * campo ao lado aceita qualquer número dentro do limite de sanidade. O teto da
 * régua acompanha o valor atual: quem digitar acima do fim da escala continua
 * enxergando onde está, em vez de ver o marcador colado na ponta.
 */
function ControleDeTaxa({
  label,
  value,
  onChange,
  min,
  teto,
}: {
  label: string
  value: number
  onChange: (valor: number) => void
  min: number
  teto: number
}) {
  const [texto, setTexto] = useState(() => (value / 1_000_000).toString())

  // Enquanto o campo está sendo digitado ele manda no texto; fora disso, segue o
  // valor salvo, que pode ter mudado por causa da régua.
  useEffect(() => {
    setTexto((atual) => (Number(atual) * 1_000_000 === value ? atual : (value / 1_000_000).toString()))
  }, [value])

  const aplicar = (bruto: string) => {
    setTexto(bruto)
    const mbps = Number(bruto.replace(',', '.'))
    if (!Number.isFinite(mbps) || mbps <= 0) return
    onChange(Math.min(teto, Math.max(min, Math.round(mbps * 1_000_000))))
  }

  const fimDaRegua = Math.max(10_000_000, value)

  return (
    <div className="py-2">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-content-primary">{label}</span>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min={min / 1_000_000}
            max={teto / 1_000_000}
            step={0.1}
            value={texto}
            onChange={(e) => aplicar(e.target.value)}
            onBlur={() => setTexto((value / 1_000_000).toString())}
            className="h-8 w-24 text-right font-mono text-xs"
          />
          <span className="text-xs text-content-secondary">Mbps</span>
        </div>
      </div>

      <Slider
        value={Math.min(value, fimDaRegua)}
        onChange={onChange}
        min={min}
        max={fimDaRegua}
        step={100_000}
      />
    </div>
  )
}

/**
 * Projeção de consumo do compartilhamento de tela.
 *
 * O número por hora sozinho não responde a pergunta que importa na hora de
 * escolher o teto, que é se a franquia do mês aguenta. Em vez de inventar uma
 * média de uso, a conta mostra quantas horas de transmissão cabem na cota
 * configurada e o que isso significa por dia.
 */
function ProjecaoDeConsumo({
  bitrate,
  altura,
  franquiaBytes,
}: {
  bitrate: number
  altura: number
  franquiaBytes: number
}) {
  const sugerida = RESOLUCOES.find((r) => r.altura === altura)
  const taxaMinima = { 720: 3_000_000, 1080: 6_000_000, 1440: 10_000_000, 2160: 20_000_000 }[
    altura
  ]
  const apertado = taxaMinima !== undefined && bitrate < taxaMinima

  // Uma sala de dez pessoas: uma transmite e nove recebem.
  const bytesPorHora = (bitrate * 9 * 3600) / 8
  const horasNaFranquia = franquiaBytes > 0 ? franquiaBytes / bytesPorHora : 0
  const horasPorDia = horasNaFranquia / 30

  return (
    <div className="mt-3 rounded-lg bg-surface-base p-3 text-[12px] leading-relaxed text-content-muted">
      <p>
        Com {formatBitrate(bitrate)}, uma transmissão para nove espectadores consome cerca de{' '}
        <strong className="text-content-secondary">{formatBytes(bytesPorHora)}</strong> por hora de
        saída.
      </p>

      {apertado ? (
        <p className="mt-1.5 text-warning">
          Para {sugerida?.rotulo} a taxa precisa de pelo menos {sugerida?.taxaSugerida}. Abaixo
          disso o codificador não abandona a resolução, ele degrada a imagem por dentro, e a tela
          fica borrada como se estivesse travando.
        </p>
      ) : null}

      {franquiaBytes > 0 ? (
        <p className="mt-1.5">
          A franquia de {formatBytes(franquiaBytes)} comporta{' '}
          <strong className="text-content-secondary">
            {horasNaFranquia >= 10 ? Math.round(horasNaFranquia) : horasNaFranquia.toFixed(1)} horas
          </strong>{' '}
          de transmissão no mês, o equivalente a{' '}
          <strong className="text-content-secondary">
            {horasPorDia >= 10 ? Math.round(horasPorDia) : horasPorDia.toFixed(1)} horas por dia
          </strong>
          . Chamadas só com voz pesam pouco perto disso: são cerca de 64 kbps por pessoa.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Resoluções oferecidas, com a taxa que cada uma pede para se sustentar.
 *
 * Os números vêm de medição em cena com movimento constante: abaixo deles o
 * codificador não larga a resolução pedida, ele derruba a imagem por dentro, e o
 * resultado é uma tela borrada que parece travada.
 */
const RESOLUCOES = [
  { altura: 720, rotulo: '720p', taxaSugerida: '3 Mbps' },
  { altura: 1080, rotulo: '1080p', taxaSugerida: '6 Mbps' },
  { altura: 1440, rotulo: '1440p', taxaSugerida: '10 Mbps' },
  { altura: 2160, rotulo: '4K', taxaSugerida: '20 Mbps' },
] as const

/**
 * Lista de servidores da instalação.
 *
 * O total na visão geral conta todos os servidores criados, e não só os que quem
 * administra participa. Ver um número maior do que o esperado é comum e não quer
 * dizer erro: normalmente é um servidor de teste esquecido, ou alguém que criou
 * o próprio depois de entrar por um convite. A lista existe para resolver essa
 * dúvida olhando, em vez de adivinhando.
 */
function GuildsTab() {
  const [servidores, setServidores] = useState<
    Awaited<ReturnType<typeof api.admin.guilds>>['guilds'] | null
  >(null)
  const [erro, setErro] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setServidores((await api.admin.guilds()).guilds)
      setErro(null)
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível carregar os servidores')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (erro) {
    return <p className="rounded-lg bg-danger-subtle p-4 text-sm text-danger">{erro}</p>
  }

  if (!servidores) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6 text-content-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-content-primary">Servidores</h2>
        <span className="text-sm text-content-muted">
          {servidores.length} {servidores.length === 1 ? 'servidor' : 'servidores'}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl bg-surface-raised">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-[13px]">
          <thead className="text-[11px] tracking-wide text-content-muted uppercase">
            <tr>
              <th className="px-4 py-2.5 font-semibold">Nome</th>
              <th className="px-4 py-2.5 font-semibold">Dono</th>
              <th className="px-4 py-2.5 text-right font-semibold">Membros</th>
              <th className="px-4 py-2.5 text-right font-semibold">Canais</th>
              <th className="px-4 py-2.5 text-right font-semibold">Mensagens</th>
              <th className="px-4 py-2.5 text-right font-semibold">Criado em</th>
            </tr>
          </thead>
          <tbody>
            {servidores.map((servidor) => (
              <tr key={servidor.id} className="border-t border-line">
                <td className="px-4 py-2.5 font-medium text-content-primary">{servidor.name}</td>
                <td className="px-4 py-2.5 text-content-secondary">
                  {servidor.ownerName}
                  {servidor.ownerUsername ? (
                    <span className="text-content-muted"> @{servidor.ownerUsername}</span>
                  ) : null}
                </td>
                <td className="px-4 py-2.5 text-right text-content-secondary tabular-nums">
                  {servidor.memberCount}
                </td>
                <td className="px-4 py-2.5 text-right text-content-secondary tabular-nums">
                  {servidor.channelCount}
                </td>
                <td className="px-4 py-2.5 text-right text-content-secondary tabular-nums">
                  {servidor.messageCount}
                </td>
                <td className="px-4 py-2.5 text-right text-content-muted tabular-nums">
                  {new Date(servidor.createdAt).toLocaleDateString('pt-BR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  )
}
